import React, { useEffect } from 'react';

import { useModal } from 'context/ModalContext';
import {
  BlogSection,
  EmailSubscriptionSection,
  FaqSection,
  JourneySection,
  PartnersSlider,
  Reviews,
  StatsSection,
  WhyCheckmateSection,
} from 'entities/home';
import { CheckmateMallPopup, HomeSearchBox, PopularLocations } from 'features/home';
import { Trans, useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_MAX_BUDGET, DEFAULT_MAX_DEPOSIT } from 'shared/constants';
import { useRouteChangeHandler } from 'shared/hooks';
import { lazyWithRetry } from 'shared/lib/lazyRetry';
import { SeoMetaTags } from 'shared/ui';

// 스크롤 아래 캐러셀 2종은 swiper 라이브러리를 쓰므로 코드 분리 — 엔트리 번들에서 swiper 제거
// (직접 경로 import는 code-splitting을 위한 예외 — barrel로 lazy하면 분리되지 않음)
const PopularRightNow = lazyWithRetry(() =>
  import('features/home/ui/PopularRightNow').then((m) => ({ default: m.PopularRightNow })),
);
const RoomsUnder500k = lazyWithRetry(() =>
  import('features/home/ui/RoomsUnder500k').then((m) => ({ default: m.RoomsUnder500k })),
);

const HomePage = () => {
  const { t, i18n } = useTranslation('home');
  const { handleRouteChange } = useRouteChangeHandler();
  const [searchParams] = useSearchParams();
  const { openModal, MODAL_TYPES } = useModal();

  useEffect(() => {
    const state = searchParams.get('state');
    if (state) {
      if (state === MODAL_TYPES.RESET.toLowerCase()) {
        const modalType = state.toUpperCase();
        openModal(modalType);
      }
    }
  }, [searchParams, MODAL_TYPES.RESET, openModal]);

  return (
    <>
      <SeoMetaTags title={t('seo.title')} description={t('seo.description')} keywords={t('seo.keywords')} />

      <CheckmateMallPopup />

      <div
        className="relative flex h-auto w-full flex-col items-start justify-start bg-cover bg-[position:55%_center] bg-no-repeat py-10 md:bg-[position:70%_center] md:py-[110px]"
        style={{
          backgroundImage: 'url(/images/home/main.webp)',
        }}
      >
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#FF2D2E] from-0% via-[#FF2D2E]/60 via-30% to-transparent to-65% md:hidden" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-[#FF2D2E] from-0% via-[#FF2D2E]/80 via-50% to-transparent to-100% md:hidden" />

        <div className="relative z-10 flex w-full flex-col items-start gap-y-5 px-5 md:gap-y-11 md:px-[120px]">
          <div className="flex w-full flex-col items-start gap-y-2 text-white">
            <h1 className="text-[20px] font-bold leading-[1.3] md:text-[32px] md:leading-tight min-[700px]:[&_br:nth-of-type(2)]:hidden">
              <Trans i18nKey="home:title" components={{ br: <br /> }} />
            </h1>
            <p
              className={`w-full text-pretty leading-[1.5] md:text-[18px] md:tracking-normal ${
                i18n.language === 'vi' ? 'text-[12px] tracking-[-0.02em]' : 'text-[13px]'
              }`}
            >
              <Trans i18nKey="home:description" components={{ strong: <strong />, br: <br /> }} />
            </p>
          </div>

          <div className="w-full md:w-auto">
            <HomeSearchBox
              onSearch={({ originalWhere, whereType, whereValue, moveIn, contractPeriod }) => {
                const params = new URLSearchParams();
                if (originalWhere && whereType && whereValue) {
                  params.append('originalWhere', originalWhere);
                  params.append('whereType', whereType);
                  params.append('whereValue', whereValue);
                }
                if (moveIn) params.append('moveIn', moveIn);
                if (contractPeriod) params.append('contractPeriod', contractPeriod);
                params.append('minBudget', '0');
                params.append('maxBudget', DEFAULT_MAX_BUDGET.toString());
                params.append('minDeposit', '0');
                params.append('maxDeposit', DEFAULT_MAX_DEPOSIT.toString());
                handleRouteChange(`/listing?${params.toString()}`);
              }}
            />
          </div>

          <PopularLocations />
        </div>
      </div>

      <StatsSection />

      <React.Suspense fallback={null}>
        <PopularRightNow />

        <RoomsUnder500k />
      </React.Suspense>

      <Reviews />

      <WhyCheckmateSection />

      <PartnersSlider />

      <JourneySection />

      <FaqSection />

      <div className="hidden md:contents">
        <BlogSection />

        <EmailSubscriptionSection />
      </div>
    </>
  );
};

export default HomePage;
